import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlaskConical, Sparkles, Leaf, Loader2, AlertTriangle, HeartHandshake } from 'lucide-react';
import api, { RARITY_META } from '../lib/api';
import RarityBadge from '../components/RarityBadge';
import { toast } from 'sonner';

const CATEGORY_CHIPS = [
  { key: 'all',       label: 'All' },
  { key: 'Birds',     label: 'Feathers' },
  { key: 'Plants',    label: 'Plants' },
  { key: 'Mushrooms', label: 'Mushrooms' },
  { key: 'Trees',     label: 'Trees' },
  { key: 'Minerals',  label: 'Minerals' },
  { key: 'Mammals',   label: 'Tracks' },
];

export default function Alchemy() {
  const [inv, setInv] = useState({ ingredients: [], potions: [], disclaimer: '' });
  const [recipes, setRecipes] = useState([]);
  const [recipesDisclaimer, setRecipesDisclaimer] = useState('');
  const [loading, setLoading] = useState(true);
  const [crafting, setCrafting] = useState(null);
  const [tab, setTab] = useState('recipes');
  const [invFilter, setInvFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    const [invRes, recRes] = await Promise.all([
      api.get('/inventory').catch(() => ({ data: { ingredients: [], potions: [] } })),
      api.get('/recipes').catch(() => ({ data: { recipes: [], disclaimer: '' } })),
    ]);
    setInv(invRes.data);
    setRecipes(recRes.data.recipes || []);
    setRecipesDisclaimer(recRes.data.disclaimer || '');
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  // Aggregate ingredient totals across rarity buckets for the recipe availability check
  const ingredientTotals = useMemo(() => {
    const t = {};
    (inv.ingredients || []).forEach(r => {
      t[r.ingredient_id] = (t[r.ingredient_id] || 0) + r.qty;
    });
    return t;
  }, [inv]);

  const canCraft = (recipe) => recipe.requires.every(req => (ingredientTotals[req.ingredient_id] || 0) >= req.qty);

  const craft = async (recipe) => {
    setCrafting(recipe.id);
    try {
      const { data } = await api.post('/craft', { recipe_id: recipe.id });
      toast.success(`Crafted ${data.name} (${data.rarity})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Craft failed');
    } finally {
      setCrafting(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="alchemy-root">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-accent">Alchemy Bench</p>
        <h2 className="font-serif text-3xl">Craft potions</h2>
        <div className="mt-2 rounded-2xl bg-amber-500/10 border border-amber-400/30 p-3 flex items-start gap-2" data-testid="alchemy-disclaimer">
          <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
          <p className="text-[11px] text-amber-100/90 leading-relaxed">
            {recipesDisclaimer || inv.disclaimer || 'Fantasy game item inspired by nature — NOT a medical claim. Effects apply to gameplay only.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Chip label="Ingredients" value={inv.ingredients.reduce((s, i) => s + (i.qty || 0), 0)} icon={Leaf} />
        <Chip label="Potions" value={inv.potions.length} icon={FlaskConical} />
        <Chip label="Recipes" value={recipes.length} icon={Sparkles} />
      </div>

      <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid="alchemy-tab-row">
        {[
          { key: 'recipes', label: 'Recipes' },
          { key: 'inventory', label: 'Ingredients' },
          { key: 'potions', label: 'My Potions' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`alchemy-tab-${t.key}`}
            className={`flex-shrink-0 text-[11px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${
              tab === t.key ? 'border-accent/60 bg-accent/10 text-accent'
                : 'border-white/10 bg-card/50 text-muted-foreground hover:text-foreground'
            }`}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div className="text-xs text-muted-foreground py-6 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>
      ) : (
        <>
          {tab === 'recipes' && (
            <div className="space-y-2" data-testid="recipes-list">
              {recipes.map(recipe => {
                const ready = canCraft(recipe);
                return (
                  <motion.div key={recipe.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    data-testid="recipe-card"
                    className={`rounded-2xl bg-card border p-3 ${ready ? 'border-accent/40' : 'border-white/10 opacity-70'}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="font-serif text-base">{recipe.name}</p>
                      <button type="button" onClick={() => craft(recipe)} disabled={!ready || crafting === recipe.id}
                        data-testid="craft-btn"
                        className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-accent/60 text-accent disabled:opacity-30 disabled:cursor-not-allowed">
                        {crafting === recipe.id ? '...' : 'Craft'}
                      </button>
                    </div>
                    {recipe.ethical !== false && (
                      <div
                        data-testid="ethical-badge"
                        className="mb-1.5 inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] uppercase tracking-widest text-emerald-200"
                        title="Every ingredient in this recipe is ethically sourced — no harvested wildlife."
                      >
                        <HeartHandshake className="w-2.5 h-2.5" />
                        Ethically sourced
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">{recipe.desc}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {recipe.requires.map(req => {
                        const have = ingredientTotals[req.ingredient_id] || 0;
                        const ok = have >= req.qty;
                        return (
                          <span key={req.ingredient_id}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              ok ? 'border-emerald-400/40 text-emerald-300' : 'border-red-400/40 text-red-300/80'
                            }`}
                          >{req.name} {have}/{req.qty}</span>
                        );
                      })}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {tab === 'inventory' && (
            <div className="space-y-2" data-testid="inventory-list">
              {/* Category chip row — Species Roles rule (iter39). */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="inventory-category-chips">
                {CATEGORY_CHIPS.map(c => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setInvFilter(c.key)}
                    data-testid={`inv-chip-${c.key}`}
                    className={`flex-shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${
                      invFilter === c.key
                        ? 'border-accent/60 bg-accent/15 text-accent'
                        : 'border-white/10 bg-card/40 text-muted-foreground hover:text-foreground'
                    }`}
                  >{c.label}</button>
                ))}
              </div>
              {(() => {
                const rows = (inv.ingredients || []).filter(r =>
                  invFilter === 'all' ? true : (r.source_category === invFilter));
                if (rows.length === 0) {
                  return (
                    <div className="rounded-3xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground">
                      <Leaf className="w-6 h-6 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                      {invFilter === 'all'
                        ? 'Discover plants, mushrooms, feathers or minerals to fill your pouch.'
                        : `No ${CATEGORY_CHIPS.find(c => c.key === invFilter)?.label || invFilter} yet.`}
                    </div>
                  );
                }
                return rows.map(r => (
                  <div key={`${r.ingredient_id}-${r.rarity}`} className="rounded-xl bg-card border border-white/5 p-3 flex items-center gap-3" data-testid="ingredient-row">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: RARITY_META[r.rarity]?.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{r.display_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{r.desc}</p>
                      {r.potion_bonus && (
                        <p className="text-[9px] uppercase tracking-widest text-accent mt-0.5">Bonus · {r.potion_bonus.replace('_', ' ')}</p>
                      )}
                    </div>
                    <span className="font-mono text-lg tabular-nums" style={{ color: RARITY_META[r.rarity]?.color }}>{r.qty}</span>
                  </div>
                ));
              })()}
            </div>
          )}

          {tab === 'potions' && (
            <div className="space-y-2" data-testid="potions-list">
              {inv.potions.length === 0 ? (
                <div className="rounded-3xl bg-card border border-white/5 p-6 text-center text-xs text-muted-foreground">
                  <FlaskConical className="w-6 h-6 mx-auto mb-2 opacity-50" strokeWidth={1.5} />
                  No potions yet — craft one from the Recipes tab.
                </div>
              ) : inv.potions.map(p => (
                <div key={p.potion_id} className="rounded-2xl bg-card border border-white/10 p-3" data-testid="potion-row">
                  <div className="flex items-center justify-between">
                    <p className="font-serif text-sm">{p.name}</p>
                    <RarityBadge rarity={p.rarity} size="sm" showParticles={false} />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{p.desc}</p>
                  <p className="mt-2 text-[9px] tracking-widest uppercase text-muted-foreground/70">{p.disclaimer}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Chip({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-3 text-center">
      <Icon className="w-4 h-4 mx-auto mb-1 text-accent" strokeWidth={1.75} />
      <p className="font-mono font-semibold text-lg tabular-nums">{value}</p>
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</p>
    </div>
  );
}
